<script>
export default {
  name: 'AdminDateRange',
  props: {
    modelValue: {
      type: Object,
      required: true,
    },
    fromLabel: {
      type: String,
      default: '시작일',
    },
    toLabel: {
      type: String,
      default: '종료일',
    },
  },
  data() {
    return {
      draftFrom: this.modelValue?.from || '',
      draftTo: this.modelValue?.to || '',
    }
  },
  watch: {
    'modelValue.from'(value) {
      if (value !== this.draftFrom) {
        this.draftFrom = value || ''
      }
    },
    'modelValue.to'(value) {
      if (value !== this.draftTo) {
        this.draftTo = value || ''
      }
    },
  },
  methods: {
    isCompleteDate(value) {
      return /^\d{4}-\d{2}-\d{2}$/.test(value)
    },
    normalizeDateInput(value) {
      const digits = String(value || '').replace(/\D/g, '').slice(0, 8)
      if (digits.length <= 4) return digits
      if (digits.length <= 6) return `${digits.slice(0, 4)}-${digits.slice(4)}`
      return `${digits.slice(0, 4)}-${digits.slice(4, 6)}-${digits.slice(6)}`
    },
    emitRange(from, to) {
      const nextValue = { from: from || '', to: to || '' }
      this.$emit('update:modelValue', nextValue)
      this.$emit('change', nextValue)
    },
    updateFrom(value) {
      this.emitRange(value, this.draftTo)
    },
    updateTo(value) {
      this.emitRange(this.draftFrom, value)
    },
    onFromTextInput(value) {
      const nextValue = this.normalizeDateInput(value)
      this.draftFrom = nextValue
      if (!nextValue || this.isCompleteDate(nextValue)) {
        this.updateFrom(nextValue)
      }
    },
    onToTextInput(value) {
      const nextValue = this.normalizeDateInput(value)
      this.draftTo = nextValue
      if (!nextValue || this.isCompleteDate(nextValue)) {
        this.updateTo(nextValue)
      }
    },
    onFromPicker(value) {
      this.draftFrom = value || ''
      this.updateFrom(this.draftFrom)
    },
    onToPicker(value) {
      this.draftTo = value || ''
      this.updateTo(this.draftTo)
    },
    openPicker(refName) {
      const input = this.$refs[refName]
      if (!input) return
      if (typeof input.showPicker === 'function') {
        input.showPicker()
        return
      }
      input.click()
    },
  },
}
</script>

<template>
  <div class="admin-date-range">
    <label class="admin-date-field">
      <span class="admin-date-field__label">{{ fromLabel }}</span>
      <input
        class="admin-date-field__input"
        type="text"
        inputmode="numeric"
        placeholder="연도-월-일"
        :value="draftFrom"
        @input="onFromTextInput($event.target.value)"
      />
      <button type="button" class="admin-date-field__picker" aria-label="시작일 선택" @click="openPicker('fromPicker')">
        <span class="mdi mdi-calendar-month-outline" aria-hidden="true"></span>
      </button>
      <input
        ref="fromPicker"
        class="admin-date-field__native"
        type="date"
        :value="isCompleteDate(draftFrom) ? draftFrom : ''"
        @input="onFromPicker($event.target.value)"
      />
    </label>
    <span class="admin-date-range__sep">~</span>
    <label class="admin-date-field">
      <span class="admin-date-field__label">{{ toLabel }}</span>
      <input
        class="admin-date-field__input"
        type="text"
        inputmode="numeric"
        placeholder="연도-월-일"
        :value="draftTo"
        @input="onToTextInput($event.target.value)"
      />
      <button type="button" class="admin-date-field__picker" aria-label="종료일 선택" @click="openPicker('toPicker')">
        <span class="mdi mdi-calendar-month-outline" aria-hidden="true"></span>
      </button>
      <input
        ref="toPicker"
        class="admin-date-field__native"
        type="date"
        :value="isCompleteDate(draftTo) ? draftTo : ''"
        @input="onToPicker($event.target.value)"
      />
    </label>
  </div>
</template>

<style scoped>
.admin-date-range {
  display: grid;
  grid-template-columns: minmax(150px, 1fr) auto minmax(150px, 1fr);
  gap: 8px;
  align-items: center;
}

.admin-date-range__sep {
  color: var(--admin-color-text-secondary);
  font-size: 13px;
}

.admin-date-field {
  position: relative;
  display: flex;
  align-items: center;
  min-width: 150px;
  height: 50px;
  border: 1px solid rgba(100, 116, 139, 0.72);
  border-radius: 10px;
  background: rgba(15, 23, 42, 0.58);
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.admin-date-field:focus-within {
  border-color: rgba(251, 191, 36, 0.72);
  box-shadow: 0 0 0 2px rgba(251, 191, 36, 0.14);
}

.admin-date-field__label {
  position: absolute;
  top: -8px;
  left: 12px;
  padding: 0 4px;
  background: #1c2230;
  color: #9fb0c9;
  font-size: 12px;
  line-height: 16px;
  pointer-events: none;
}

.admin-date-field__input {
  width: 100%;
  min-width: 0;
  height: 100%;
  padding: 14px 42px 8px 12px;
  border: 0;
  outline: 0;
  background: transparent;
  color: #dbe3ef;
  font: inherit;
  font-size: 14px;
  font-weight: 600;
  color-scheme: dark;
}

.admin-date-field__input::placeholder {
  color: #9fb0c9;
  opacity: 0.9;
}

.admin-date-field__picker {
  position: absolute;
  top: 50%;
  right: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  padding: 0;
  border: 0;
  border-radius: 6px;
  background: transparent;
  color: #dbe3ef;
  cursor: pointer;
  transform: translateY(-50%);
}

.admin-date-field__picker:hover {
  background: rgba(148, 163, 184, 0.12);
}

.admin-date-field__picker .mdi {
  font-size: 19px;
  line-height: 1;
}

.admin-date-field__native {
  position: absolute;
  right: 10px;
  bottom: 0;
  width: 1px;
  height: 1px;
  border: 0;
  opacity: 0;
  pointer-events: none;
}

.admin-date-field__native::-webkit-calendar-picker-indicator {
  filter: invert(0.9) saturate(0.2) brightness(1.25);
  opacity: 0.92;
  cursor: pointer;
}
</style>
